<template>
  <div
    class="tw-flex tw-items-stretch tw-gap-[8px]"
    :class="propertyPath ? 'tw-cursor-pointer' : ''"
    :role="propertyPath ? 'link' : null"
    :tabindex="propertyPath ? 0 : null"
    @click="handleCardClick($event)"
    @keydown.enter.prevent="handleCardClick($event)"
    @keydown.space.prevent="handleCardClick($event)"
  >
    <img
      :src="property.mainImage"
      class="tw-min-h-[100px] tw-w-[110px] tw-rounded-[8px] tw-object-cover"
    />

    <div
      class="tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-text-[14px]"
    >
      <div class="tw-text-gray-500">
        {{ property.addressShort }}
      </div>

      <div
        class="tw-grid tw-max-w-[328px] tw-grid-cols-2 tw-gap-x-4 tw-gap-y-[4px] tw-text-uhblue-500 min-[392px]:tw-flex min-[392px]:tw-items-center min-[392px]:tw-justify-between"
      >
        <div class="tw-w-[25px]">
          <img
            v-lazy="
              property.type === 'house'
                ? cdnAsset('svg/house-blue-icon.svg')
                : cdnAsset('svg/apartment-blue-icon.svg')
            "
            alt="Building Icon"
            class="tw-h-6 tw-w-[20px]"
            :src="cdnAsset('svg/no-image.svg')"
          />
        </div>

        <div class="tw-flex tw-items-center">
          <div class="tw-w-[25px]">
            <img
              v-lazy="cdnAsset('svg/bedroom-icon.svg')"
              alt="Bedroom Icon"
              class="tw-h-6 tw-w-[20px]"
              :src="cdnAsset('svg/no-image.svg')"
            />
          </div>

          <span class="tw-text-[#3B4143]">{{
            property.roomsAvailableCount
          }}</span>
        </div>

        <div class="tw-flex tw-items-center">
          <div class="tw-w-[25px]">
            <img
              v-lazy="cdnAsset('svg/bathtub-icon.svg')"
              alt="Bathtub Icon"
              class="tw-h-[27px] tw-w-[25px]"
              :src="cdnAsset('svg/no-image.svg')"
            />
          </div>

          <span class="tw-text-[#3B4143]">{{ property.bathroomCount }}</span>
        </div>

        <div class="tw-font-semibold tw-text-uhblue-500">
          £{{ property.cheapestRoomPrice }}
          <span class="tw-font-normal tw-text-[#3B4143]">pppw</span>
        </div>
      </div>

      <div
        v-if="hasEnquiryAlreadySent"
        class="tw-w-full tw-max-w-[328px] tw-text-[12px] tw-font-semibold tw-text-[#3B4143]"
      >
        Enquiry sent to property
      </div>

      <div
        v-if="!hasEnquiryAlreadySent"
        class="tw-flex tw-w-full tw-max-w-[328px] tw-items-center tw-gap-[8px]"
      >
        <button
          type="button"
          class="tw-flex tw-h-[40px] tw-flex-1 tw-items-center tw-justify-center tw-gap-[6px] tw-rounded-[999px] tw-border tw-px-[10px] tw-text-[14px] tw-font-semibold"
          :class="
            currentGroupVote === 'yes'
              ? 'tw-border-[#497D00] tw-bg-[#F0FDF4] tw-text-[#497D00]'
              : 'tw-border-[#D1D5DB] tw-bg-white tw-text-[#3B4143]'
          "
          :disabled="housemateVotingLocked"
          :aria-pressed="currentGroupVote === 'yes'"
          @click.stop.prevent="submitHousemateVote('yes')"
        >
          <ThumbsUp
            :stroke-color="currentGroupVote === 'yes' ? '#497D00' : '#6B7280'"
          />
          <span>{{ Number(property.groupVoteYesCount ?? 0) }}</span>
        </button>

        <button
          type="button"
          class="tw-flex tw-h-[40px] tw-flex-1 tw-items-center tw-justify-center tw-gap-[6px] tw-rounded-[999px] tw-border tw-px-[10px] tw-text-[14px] tw-font-semibold"
          :class="
            currentGroupVote === 'no'
              ? 'tw-border-[#E7000B] tw-bg-[#FEF2F2] tw-text-[#E7000B]'
              : 'tw-border-[#D1D5DB] tw-bg-white tw-text-[#3B4143]'
          "
          :aria-pressed="currentGroupVote === 'no'"
          :disabled="housemateVotingLocked"
          @click.stop.prevent="submitHousemateVote('no')"
        >
          <ThumbsDown
            :stroke-color="currentGroupVote === 'no' ? '#E7000B' : '#6B7280'"
          />
          <span>{{ Number(property.groupVoteNoCount ?? 0) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ThumbsUp from '@components/Student/SVG/ThumbsUp.vue';
import ThumbsDown from '@components/Student/SVG/ThumbsDown.vue';

export default {
  name: 'PropertyList',
  components: {
    ThumbsUp,
    ThumbsDown,
  },
  props: {
    property: {
      default: function () {
        return {};
      },
      required: true,
      type: Object,
    },
    housemateGroupActive: {
      default: false,
      type: Boolean,
    },
    housemateGroupId: {
      default: null,
      type: [Number, String],
    },
    isShortlistView: {
      default: false,
      type: Boolean,
    },
  },
  computed: {
    propertyPath() {
      const path = this.property?.path;
      return typeof path === 'string' && path.trim().length > 0 ? path : null;
    },

    showHousemateVoteActions() {
      return Boolean(
        this.housemateGroupActive &&
          this.housemateGroupId &&
          !this.isShortlistView
      );
    },

    currentGroupVote() {
      return typeof this.property?.groupVote === 'string'
        ? this.property.groupVote.toLowerCase()
        : null;
    },
    displayLastEnquiredAt() {
      return (
        this.property?.lastEnquiredAt ??
        this.property?.housemateGroupLastEnquiredAt ??
        this.property?.housemate_group_last_enquired_at ??
        null
      );
    },
    groupLastEnquiredAt() {
      return (
        this.property?.housemateGroupLastEnquiredAt ??
        this.property?.housemate_group_last_enquired_at ??
        null
      );
    },
    hasEnquiryAlreadySent() {
      return Boolean(this.groupLastEnquiredAt);
    },
    housemateVotingLocked() {
      return !this.showHousemateVoteActions || this.hasEnquiryAlreadySent;
    },
  },
  methods: {
    handleCardClick(event) {
      if (!this.propertyPath) {
        return;
      }

      const interactiveTarget = event?.target?.closest?.(
        'button, a, input, select, textarea, [role="button"]'
      );

      if (interactiveTarget) {
        return;
      }

      window.location.assign(this.propertyPath);
    },

    submitHousemateVote(vote) {
      if (this.housemateVotingLocked || !this.showHousemateVoteActions) {
        return;
      }

      if (!['yes', 'no'].includes(vote)) {
        return;
      }

      this.$emit('housemate-group-vote', {
        propertyId: this.property.id,
        vote,
      });
    },
  },
};
</script>
